import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Volume2, CalendarCheck, GraduationCap, KeyRound, Users, Award, LayoutDashboard, Check, X, ChevronDown,
  ArrowRight, PlayCircle, Target, ShieldCheck, Timer, Compass, MessageCircle, Clock,
} from 'lucide-react';
import { useReferral } from '@/hooks/useReferral';

/*
  /masterclass — replay de la masterclass «Cómo acceder al mercado inmobiliario sin comprar un piso entero» (22/10/2026).

  Estructura: título + promesa · vídeo · CTA a agendar llamada con Toni (Calendly) · landing de venta del club.
  La parte de venta sigue la ecuación de valor de Hormozi (resultado deseado × probabilidad percibida ÷ tiempo × esfuerzo)
  aplicada a la LLAMADA, que es lo que se vende aquí: qué consigues, por qué es probable, en cuánto tiempo y con qué esfuerzo,
  una oferta apilada (lo que trae el club) y reversión del riesgo (llamada gratuita, sin compromiso, sales con claridad).
  Reglas FHC: club privado y educativo; sin precios, cifras, rentabilidades, retornos, promesas ni tokens; el socio decide;
  nada de «inversión» ni «ingresos pasivos»; Amaroo no es sede. Página por enlace directo (noindex).

  Vídeo: el ID de Vimeo va en VIMEO_ID (los números del final de vimeo.com/123456789).
  Si el vídeo es privado con hash (vimeo.com/123456789/abcdef1234), pon también VIMEO_HASH.
*/

const VIMEO_ID = '1238277909';  // ← ID del vídeo de Vimeo del replay (vimeo.com/1238277909)
const VIMEO_HASH = '';   // ← solo si el enlace de Vimeo lleva un segundo código (vídeo privado)

const CALENDLY_URL = 'https://calendly.com/toni-freedomhomes/30min';

const GOLD = 'text-[#c9a84c]';
const serif = { fontFamily: "'Playfair Display', Georgia, serif" };

const reveal = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.55, ease: 'easeOut' },
};

// Enlace a la agenda con el origen (y el broker que compartió el enlace, si lo hay) para que el equipo sepa de dónde viene
function bookingUrl(ref) {
  const p = new URLSearchParams({ utm_source: 'freedomhomes', utm_medium: 'landing', utm_campaign: 'replay-masterclass' });
  if (ref) p.set('utm_content', ref);
  return `${CALENDLY_URL}?${p.toString()}`;
}

const RECAP = [
  'Por qué el mercado inmobiliario de calidad ha estado siempre en pocas manos.',
  'Cómo un club privado permite formar parte de él sin comprar una vivienda entera.',
  'Cómo se forma un miembro desde cero antes de tomar cualquier decisión.',
  'Cómo es Freedom Homes Club por dentro y cuáles son los pasos para entrar.',
];

// Ecuación de valor de la llamada: lo que la hace valiosa para quien la agenda
const VALUE = [
  { icon: Target, title: 'Sales con un plan claro', text: 'Qué camino tiene sentido para ti dentro del club según tu punto de partida y tus objetivos. Sin teoría genérica: tu caso.' },
  { icon: ShieldCheck, title: 'Con quien lo hace cada día', text: 'Hablas con el equipo que forma a los miembros y presenta las oportunidades del club. Preguntas lo que quieras y te respondemos con datos.' },
  { icon: Timer, title: 'En 30 minutos', text: 'Lo que llevaría semanas de búsqueda por tu cuenta, resuelto en media hora. Y, si entras, empiezas tu formación ese mismo día.' },
  { icon: Compass, title: 'Sin tener que saber nada antes', text: 'No necesitas experiencia ni preparar nada. Solo tus dudas. El club está pensado para empezar desde cero.' },
];

const CALL_STEPS = [
  ['Tu punto de partida', 'Nos cuentas qué te ha parecido la masterclass, dónde estás hoy y qué te gustaría conseguir.'],
  ['El club por dentro', 'Te enseñamos cómo funciona en la práctica: la formación, la comunidad y cómo se presentan las oportunidades a los miembros.'],
  ['Todas tus dudas', 'Preguntas sin filtro, también sobre el precio de cada membresía y lo que incluye.'],
  ['Si encaja, o no', 'Vemos juntos qué nivel tiene sentido para ti. Y si el club no es para ti, te lo diremos.'],
];

// Lo que trae el club (la oferta apilada, sin precios)
const STACK = [
  { icon: GraduationCap, title: 'FHC Academy', text: 'Un recorrido de 7 módulos y 47 clases, desde el primer concepto hasta el análisis de una operación real, con diploma del club.' },
  { icon: KeyRound, title: 'Acceso a oportunidades off-market', text: 'Operaciones inmobiliarias reales que el equipo y sus partners trabajan fuera de los portales, presentadas en el área privada con su documentación.' },
  { icon: Users, title: 'Comunidad y sesiones en directo', text: 'Miembros, brokers y el equipo fundador compartiendo análisis, dudas y experiencia.' },
  { icon: Award, title: 'Bootcamp y certificación Broker Digital', text: 'Con Premium: bootcamp presencial y certificación privada del club para presentar el modelo con rigor.' },
  { icon: LayoutDashboard, title: 'Tu área privada', text: 'Tu panel de miembro con la academia, tus documentos, las oportunidades y un asistente de IA disponible 24/7.' },
];

const COMPARE = [
  ['Por dónde empiezas', 'Buscando por tu cuenta, entre portales y contactos', 'Formación desde cero y acompañamiento del equipo'],
  ['Qué oportunidades ves', 'Lo que ya ha llegado a un portal', 'Operaciones trabajadas fuera de los portales'],
  ['Qué tienes que comprar', 'Una vivienda entera, con todo lo que implica', 'Eliges en qué operación participas y cuánto aportas'],
  ['Quién analiza', 'Tú solo', 'Cada oportunidad llega con su documentación y su estudio de mercado'],
  ['Quién decide', 'Tú, sin red', 'Tú, con información y con una comunidad con criterio'],
];

const FOR_YES = [
  'Has visto la masterclass y quieres saber cómo sería en tu caso.',
  'Quieres formar parte del mercado inmobiliario sin comprar una vivienda entera.',
  'Prefieres formarte y decidir tú, con información.',
  'Valoras una comunidad y un equipo que dan la cara.',
];
const FOR_NO = [
  'Buscas dinero rápido o resultados garantizados.',
  'Quieres que otra persona decida por ti.',
  'No tienes un rato a la semana para formarte.',
];

const TEAM = [
  { name: 'Antonio Gimeno', role: 'CCO y Director Comercial', img: '/team/antonio-gimeno.jpg' },
  { name: 'Daniel Serrano', role: 'COO', img: '/team/daniel-serrano.jpg' },
  { name: 'Luna Trejos', role: 'Directora de Formación y Comunidad', img: '/team/luna-trejos.jpg' },
];

const FAQ = [
  ['¿La llamada tiene algún coste?', 'No. Son 30 minutos con el equipo, sin coste y sin compromiso.'],
  ['¿Me van a presionar para entrar?', 'No. La llamada sirve para que tengas claridad. Si el club no encaja contigo, te lo diremos, y si encaja, la decisión sigue siendo tuya.'],
  ['¿Necesito experiencia previa?', 'No. La formación empieza desde cero y avanza a tu ritmo, con acompañamiento del equipo.'],
  ['¿Cuánto cuesta la membresía?', 'Hay tres niveles, cada uno con su cuota anual. Te los explicamos en la llamada para recomendarte el que de verdad encaja contigo.'],
  ['¿Qué incluye la membresía?', 'Tu acceso al club: formación, comunidad, eventos y el área privada donde se presentan las oportunidades. Participar en alguna es una decisión aparte y voluntaria: tú eliges, después de formarte y con toda la documentación delante.'],
  ['¿Puedo entrar desde fuera de España?', 'Sí. El club está abierto a miembros de España y Latinoamérica. La formación y la comunidad son online.'],
];

function useNoIndex(title) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => { document.title = prev; meta.remove(); };
  }, [title]);
}

function CtaButton({ href, children = 'Agendar mi llamada', size = 'lg', testid = 'cta-agenda' }) {
  const pad = size === 'lg' ? 'px-7 py-4 text-base' : 'px-5 py-2.5 text-sm';
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className={`group inline-flex max-w-full items-center justify-center gap-2.5 rounded-full text-center bg-gradient-to-r from-[#c9a84c] to-[#e3c87a] ${pad} font-semibold text-[#0a1225] shadow-[0_10px_40px_-10px_rgba(201,168,76,0.6)] transition-transform hover:-translate-y-0.5`}>
      <CalendarCheck size={size === 'lg' ? 19 : 16} /> {children}
      <ArrowRight size={size === 'lg' ? 18 : 15} className="transition-transform group-hover:translate-x-0.5" />
    </a>
  );
}

function Reassurance({ className = '' }) {
  return (
    <p className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-400 ${className}`}>
      <span className="inline-flex items-center gap-1.5"><Clock size={13} className={GOLD} /> 30 minutos</span>
      <span className="inline-flex items-center gap-1.5"><MessageCircle size={13} className={GOLD} /> Con el equipo</span>
      <span className="inline-flex items-center gap-1.5"><Check size={13} className={GOLD} /> Gratis y sin compromiso</span>
    </p>
  );
}

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <motion.div {...reveal} className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
      {eyebrow && <p className={`mb-3 text-[11px] font-semibold uppercase tracking-[0.3em] ${GOLD}`}>{eyebrow}</p>}
      <h2 style={serif} className="text-3xl leading-tight text-white md:text-[2.6rem]">{title}</h2>
      {sub && <p className="mt-4 text-base leading-relaxed text-gray-400 md:text-lg">{sub}</p>}
    </motion.div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.07]">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="flex min-h-[44px] w-full items-center justify-between gap-4 py-5 text-left text-white hover:text-[#e3c87a]">
        <span className="font-medium">{q}</span>
        <ChevronDown size={18} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${GOLD}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden pb-5 text-sm leading-relaxed text-gray-400">{a}</motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

function Video({ muted, onUnmute }) {
  if (!VIMEO_ID) {
    return (
      <div className="relative flex items-center justify-center bg-gradient-to-br from-[#13284a] to-[#060d1b]" style={{ paddingBottom: '56.25%' }} data-testid="video-pendiente">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <PlayCircle size={56} className={GOLD} strokeWidth={1.2} />
          <p className="text-sm text-gray-300">El replay estará disponible aquí en breve.</p>
        </div>
      </div>
    );
  }
  const hash = VIMEO_HASH ? `h=${VIMEO_HASH}&` : '';
  return (
    <>
      <div className="relative" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={`https://player.vimeo.com/video/${VIMEO_ID}?${hash}autoplay=1&muted=${muted ? 1 : 0}&playsinline=1&title=0&byline=0&portrait=0&pip=0&dnt=1`}
          className="absolute inset-0 h-full w-full" frameBorder="0" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
          title="Masterclass: cómo acceder al mercado inmobiliario sin comprar un piso entero" data-testid="video" />
      </div>
      {muted && (
        <button type="button" onClick={onUnmute} data-testid="unmute-video"
          className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-[#c9a84c] px-4 py-2 text-xs font-semibold text-[#0a1225] shadow-lg hover:bg-[#d4b85c]">
          <Volume2 size={14} /> Activar sonido
        </button>
      )}
    </>
  );
}

export default function MasterclassReplay() {
  const { referralCode } = useReferral();
  const [muted, setMuted] = useState(true);
  const [showBar, setShowBar] = useState(false);
  useNoIndex('Replay de la masterclass · Freedom Homes Club');
  const book = bookingUrl(referralCode);

  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 900);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#060d1b] text-white" data-testid="masterclass-replay">
      <header className="sticky top-0 z-40 border-b border-white/[0.05] bg-[#060d1b]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <a href="/" aria-label="Freedom Homes Club"><img src="/logo-fhc-sm.png" alt="Freedom Homes Club" className="h-11 w-auto" /></a>
          <CtaButton href={book} size="sm" testid="cta-header">Agendar llamada</CtaButton>
        </div>
      </header>

      {/* Título + promesa + vídeo + CTA */}
      <section className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[620px] bg-[radial-gradient(ellipse_at_top,rgba(201,168,76,0.16),transparent_60%)]" />
        <div className="relative mx-auto max-w-5xl px-5 pb-16 pt-10 md:pt-16">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
            <p className="inline-flex items-center gap-2 rounded-full border border-[#c9a84c]/30 bg-[#c9a84c]/[0.08] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#e3c87a]">
              <PlayCircle size={12} /> Replay de la masterclass
            </p>
            <h1 style={serif} className="mx-auto mt-6 max-w-4xl text-[2.1rem] leading-[1.12] md:text-6xl">
              Cómo acceder al mercado inmobiliario
              <span className="mt-2 block text-transparent bg-clip-text bg-gradient-to-r from-[#c9a84c] via-[#e3c87a] to-[#c9a84c]">sin comprar un piso entero.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-gray-300 md:text-lg">
              El equipo fundador de Freedom Homes Club te enseña cómo un club privado abre la puerta a operaciones inmobiliarias
              reales que normalmente quedan fuera del alcance de un particular, con formación desde cero y decidiendo tú en cada paso.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="mt-10">
            <div className="relative overflow-hidden rounded-2xl border border-[#c9a84c]/25 bg-black shadow-[0_30px_120px_-30px_rgba(201,168,76,0.45)]">
              <Video muted={muted} onUnmute={() => setMuted(false)} />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }} className="mt-8 text-center">
            <CtaButton href={book} testid="cta-video">Agendar mi llamada con el equipo</CtaButton>
            <Reassurance className="mt-4" />
          </motion.div>

          <motion.div {...reveal} className="mx-auto mt-12 max-w-3xl rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 md:p-8">
            <p className={`text-[11px] font-semibold uppercase tracking-[0.25em] ${GOLD}`}>En la masterclass</p>
            <ul className="mt-4 grid gap-3 md:grid-cols-2">
              {RECAP.map((d) => (
                <li key={d} className="flex items-start gap-3 text-gray-300">
                  <span className="mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#c9a84c]/15"><Check size={12} className={GOLD} strokeWidth={3} /></span>
                  <span className="leading-relaxed">{d}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Por qué agendar la llamada (ecuación de valor) */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <SectionTitle eyebrow="Por qué agendar la llamada" title={<>Has visto cómo funciona. <span className={GOLD}>Ahora, cómo sería en tu caso.</span></>}
            sub="La masterclass explica el club. La llamada lo aterriza a tu situación: qué camino tiene sentido para ti y por dónde empezar." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUE.map((v, i) => (
              <motion.div key={v.title} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7">
                <v.icon size={26} className={GOLD} />
                <h3 className="mt-5 text-lg font-semibold text-white">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Qué pasa en la llamada */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Qué pasa en la llamada" title="30 minutos. Cuatro pasos. Cero presión." />
          <div className="grid gap-5 md:grid-cols-2">
            {CALL_STEPS.map(([t, d], i) => (
              <motion.div key={t} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }}
                className="flex gap-5 rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7">
                <span style={serif} className={`text-4xl leading-none ${GOLD}`}>{i + 1}</span>
                <div>
                  <h3 className="text-lg font-semibold text-white">{t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-400">{d}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...reveal} className="mx-auto mt-10 max-w-2xl rounded-3xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.05] p-7 text-center">
            <p style={serif} className="text-2xl text-white">Nuestro compromiso</p>
            <p className="mt-3 leading-relaxed text-gray-300">
              Sales de la llamada sabiendo exactamente si el club encaja contigo y cuál sería tu siguiente paso. Si no encaja, te lo diremos.
              Tu tiempo vale, y el nuestro también.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Lo que trae el club */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-5">
          <SectionTitle eyebrow="Lo que te espera dentro" title="Todo lo que necesitas para entrar con criterio."
            sub="Esto es lo que encuentra un miembro desde el primer día. En la llamada te explicamos qué incluye cada nivel." />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {STACK.map((b, i) => (
              <motion.div key={b.title} {...reveal} transition={{ ...reveal.transition, delay: (i % 3) * 0.08 }}
                className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7 transition-colors hover:border-[#c9a84c]/30">
                <b.icon size={26} className={GOLD} />
                <h3 className="mt-5 text-lg font-semibold text-white">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">{b.text}</p>
              </motion.div>
            ))}
            <motion.div {...reveal} className="flex flex-col items-center justify-center rounded-3xl border border-[#c9a84c]/30 bg-gradient-to-b from-[#c9a84c]/[0.08] to-transparent p-7 text-center">
              <p style={serif} className="text-xl text-white">¿Por dónde empezarías tú?</p>
              <p className="mt-2 text-sm text-gray-400">Lo vemos juntos en 30 minutos.</p>
              <div className="mt-5"><CtaButton href={book} size="sm" testid="cta-stack">Agendar llamada</CtaButton></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Comprar un piso entero frente al club */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="La diferencia" title="Comprar un piso entero no es la única puerta." />
          <motion.div {...reveal} className="overflow-hidden rounded-3xl border border-white/[0.08]">
            <div className="grid grid-cols-[1fr_1fr] border-b border-white/[0.08] bg-white/[0.03] text-sm font-semibold md:grid-cols-[0.8fr_1fr_1fr]">
              <span className="hidden px-5 py-4 text-gray-500 md:block" />
              <span className="px-5 py-4 text-gray-300">Por tu cuenta</span>
              <span className={`px-5 py-4 ${GOLD}`}>Con Freedom Homes Club</span>
            </div>
            {COMPARE.map(([k, a, b]) => (
              <div key={k} className="grid grid-cols-[1fr_1fr] border-b border-white/[0.05] text-sm last:border-b-0 md:grid-cols-[0.8fr_1fr_1fr]">
                <span className="col-span-2 px-5 pt-4 text-[11px] font-semibold uppercase tracking-wider text-gray-500 md:col-span-1 md:py-4 md:text-xs md:normal-case md:tracking-normal md:text-gray-400">{k}</span>
                <span className="flex gap-2 px-5 py-4 text-gray-400"><X size={16} className="mt-0.5 flex-shrink-0 text-gray-600" />{a}</span>
                <span className="flex gap-2 px-5 py-4 text-gray-200"><Check size={16} className={`mt-0.5 flex-shrink-0 ${GOLD}`} strokeWidth={3} />{b}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Para quién es */}
      <section className="border-y border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Para quién es" title="El club no es para todo el mundo. Y está bien que así sea." />
          <div className="grid gap-5 md:grid-cols-[1.4fr_1fr]">
            <motion.div {...reveal} className="rounded-3xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.05] p-7">
              <p className="text-lg font-semibold text-white">Agenda la llamada si…</p>
              <ul className="mt-4 space-y-3">
                {FOR_YES.map((t) => <li key={t} className="flex gap-3 text-gray-300"><Check size={18} className={`mt-0.5 flex-shrink-0 ${GOLD}`} strokeWidth={3} />{t}</li>)}
              </ul>
            </motion.div>
            <motion.div {...reveal} className="rounded-3xl border border-white/[0.06] bg-white/[0.02] p-7">
              <p className="text-lg font-semibold text-white">Mejor no la agendes si…</p>
              <ul className="mt-4 space-y-3">
                {FOR_NO.map((t) => <li key={t} className="flex gap-3 text-gray-400"><X size={18} className="mt-0.5 flex-shrink-0 text-gray-500" />{t}</li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Equipo */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-5xl px-5">
          <SectionTitle eyebrow="Quién está detrás" title="Un equipo fundador que da la cara." />
          <div className="mx-auto flex max-w-3xl flex-wrap justify-center gap-5">
            {TEAM.map((p, i) => (
              <motion.div key={p.name} {...reveal} transition={{ ...reveal.transition, delay: i * 0.08 }} className="w-[calc(50%-10px)] text-center md:w-[calc(33.333%-13.34px)]">
                <img src={p.img} alt={p.name} loading="lazy" className="mx-auto aspect-square w-full max-w-[180px] rounded-3xl border border-white/[0.08] object-cover grayscale-[20%]" />
                <p className="mt-4 font-semibold text-white">{p.name}</p>
                <p className="mt-0.5 text-xs text-gray-400">{p.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Preguntas */}
      <section className="border-t border-white/[0.05] bg-[#081325] py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-5">
          <SectionTitle eyebrow="Preguntas frecuentes" title="Antes de agendar." />
          <motion.div {...reveal} className="border-t border-white/[0.07]">
            {FAQ.map(([q, a]) => <FaqItem key={q} q={q} a={a} />)}
          </motion.div>
        </div>
      </section>

      {/* CTA final */}
      <section className="relative overflow-hidden pb-24 pt-16">
        <div className="mx-auto max-w-4xl px-5">
          <motion.div {...reveal} className="relative overflow-hidden rounded-[2rem] border border-[#c9a84c]/30 bg-gradient-to-br from-[#13284a] via-[#0a1628] to-[#060d1b] px-6 py-14 text-center md:px-14">
            <div className="pointer-events-none absolute -top-24 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-[#c9a84c]/20 blur-3xl" />
            <p className={`relative text-[11px] font-semibold uppercase tracking-[0.3em] ${GOLD}`}>Tu siguiente paso</p>
            <h2 style={serif} className="relative mt-4 text-3xl leading-tight md:text-5xl">Ya sabes cómo funciona.<br />Ahora descubre si es para ti.</h2>
            <p className="relative mx-auto mt-5 max-w-xl text-gray-300">
              Elige día y hora para una conversación privada de 30 minutos con el equipo de Freedom Homes Club.
            </p>
            <div className="relative mt-8"><CtaButton href={book} testid="cta-final" /></div>
            <Reassurance className="relative mt-4" />
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-white/[0.05] py-10 pb-24 md:pb-10">
        <div className="mx-auto max-w-4xl space-y-3 px-5 text-center text-[11px] leading-relaxed text-gray-500">
          <p>
            Freedom Homes Club es un club privado de formación y comunidad. La información de esta página es divulgativa:
            no constituye una oferta pública ni garantiza resultados. Participar en cualquier oportunidad del club es una
            decisión independiente de la membresía, voluntaria y sujeta a su documentación.
          </p>
          <p>© {new Date().getFullYear()} Freedom Life Club LLC · info@freedomhomes.club · <a href="/terms-conditions" className="hover:text-[#c9a84c]">Términos y condiciones</a> · <a href="/privacy-policy-fhc" className="hover:text-[#c9a84c]">Privacidad</a></p>
        </div>
      </footer>

      <AnimatePresence>
        {showBar && (
          <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} transition={{ duration: 0.25 }}
            className="fixed inset-x-0 bottom-0 z-50 border-t border-[#c9a84c]/20 bg-[#060d1b]/95 px-4 py-3 backdrop-blur md:hidden">
            <a href={book} target="_blank" rel="noopener noreferrer" data-testid="cta-sticky"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#c9a84c] to-[#e3c87a] py-3.5 font-semibold text-[#0a1225]">
              <CalendarCheck size={17} /> Agendar mi llamada
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
